import { Button, Dialog } from '@material-ui/core';
import { modalsHide } from 'actions';
import { useField, useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useGetRelistIndexesQuery, useRelistVehicleMutation } from 'services/api/admin-auction-vehicles';
import { closeRelistDialog } from 'services/auctionVehicles/auctionVehiclesSlice';
import styled from 'styled-components';

const parseMaxOfferValue = (value: string) => {
  const [first, second] = value?.split('/') ?? [];
  const maxOffer = Number(first?.replace(/\D/g, ''));
  const proxyOffer = Number(second?.replace(/\D/g, ''));
  return [maxOffer, proxyOffer];
};

const StyledDialog = styled(Dialog)``;

const Wrapper = styled.form`
  display: flex;
  flex-direction: column;
  padding: 24px;
  width: 100%;
  height: 100%;
  gap: 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 4px 4px 0px #00000040;
  height: 44px;
`;

const Text = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #000;
`;

type Props = {
  open: boolean;
  close: () => void;
  vehicleId: number;
};

export const ConfirmIndexesDialog = ({ open, close, vehicleId }: Props) => {
  const form = useForm();

  const {
    input: { value: selectedAuctionId }
  } = useField('auctionId');

  const {
    input: { value: keepPrevious }
  } = useField('keepPreviousIndex');

  const dispatch = useDispatch();

  const { data, isFetching } = useGetRelistIndexesQuery(
    { vehicleId, auctionId: selectedAuctionId?.value, keepPrevious },
    { skip: !open || !selectedAuctionId?.value }
  );

  const [relistVehicle] = useRelistVehicleMutation();

  if (!data || isFetching) {
    return null;
  }

  const values = form.getState().values;

  const handleConfirm = () => {
    const [maxOffer, maxProxy] = parseMaxOfferValue(values.maxOffer!);
    relistVehicle({
      auctionId: values.auctionId?.value,
      vehicleId,
      maxOffer,
      maxProxy,
      newReserve: Number(values.reserve_price) ?? null,
      initialReserve: values.initialReserve,
      finalReserve: values.finalReserve,
      aaCounter: values.aaCounter,
      minSellerWillTake: values.minSellerWillTake,
      difference: values.difference,
      notes: values.notes,
      buyer: values.buyer?.value,
      seller: values.seller,
      blueBookValue: values.blueBookValue,
      startingBid: Number(values.starting_bid),
      preserveBids: values.preserveBids,
      keepPreviousIndex: values.keepPreviousIndex
    });
    close();
    dispatch(closeRelistDialog());
    dispatch(modalsHide(`cancel-relist-${vehicleId}`));
  };

  return (
    <StyledDialog open={open} onClose={close}>
      <Wrapper>
        <Text style={{ fontSize: 18, fontWeight: 700 }}>Confirm Vehicle ID Change</Text>
        <Text>Previous ID: {data?.previousIndex}</Text>
        <Text>New ID: {data?.newIndex}</Text>
        {!data?.numbersLocked && (
          <Text style={{ color: '#757575' }}>Note: It may change all vehicle numbers in the auction.</Text>
        )}
        <ButtonWrapper>
          <StyledButton
            variant="contained"
            color="default"
            onClick={e => {
              e.preventDefault();
              close();
            }}
          >
            CANCEL
          </StyledButton>
          <StyledButton
            disabled={!data}
            onClick={handleConfirm}
            type="button"
            variant="contained"
            color="primary"
            size="small"
          >
            CONFIRM
          </StyledButton>
        </ButtonWrapper>
      </Wrapper>
    </StyledDialog>
  );
};
